import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaInstagram } from "react-icons/fa";
import { db } from "@lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import useEmblaCarousel from "embla-carousel-react";

export const SectionOrganisasi = () => {
  const [eventImage, setEventImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    dragFree: true,
    containScroll: "trimSnaps",
  });

  useEffect(() => {
    const fetchEventImage = async () => {
      try {
        const eventRef = doc(db, "semuaKegiatan", "JOTR");
        const eventDoc = await getDoc(eventRef);

        if (eventDoc.exists()) {
          const data = eventDoc.data();
          setEventImage(data.image || "https://via.placeholder.com/800x400");
        } else {
          setError("Dokumen Jakarta Open Trail Run tidak ditemukan");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEventImage();
  }, []);

  // Deteksi lebar layar untuk mengaktifkan Embla Carousel di bawah 1024px
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading || error) {
    return (
      <section id="event-section" className="w-full py-20 bg-lime-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-slate-600 text-lg">
              {loading ? "Memuat data..." : error || "Terjadi kesalahan"}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="event-section"
      className="w-full py-20 relative z-0 bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: "url('/bg/bghero11.jpg')",
      }}
    >
      <div className="absolute inset-0 bg-lime-50/90 z-0"></div>
      <div className="container mx-auto px-4 sm:px-4 lg:px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.h2
              className="text-2xl font-black uppercase tracking-wider text-lime-600"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              Event Organizing
            </motion.h2>
            <motion.h3
              className="text-3xl md:text-5xl font-black uppercase text-lime-600 mt-2"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              Committees
            </motion.h3>
          </div>

          {/* Kondisional: Embla Carousel untuk <1024px, Flex asli untuk ≥1024px */}
          {isSmallScreen ? (
            <div className="overflow-hidden px-4 sm:px-4" ref={emblaRef}>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-0">
                {/* Anggota Tim 1 */}
                <motion.div
                  className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.0, ease: "easeOut" }}
                >
                  <div className="avatar">
                    <div className="mask mask-squircle w-24 md:w-40">
                      <img src="/team/rudi.png" alt="Rudi" />
                    </div>
                  </div>
                  <div className="text-left sm:text-center">
                    <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                      Rudi Rohmansyah
                    </h3>
                    <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                      Senior Advisor
                    </p>
                    <a
                      href="https://www.instagram.com/rudroch"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                    >
                      <FaInstagram className="text-lg md:text-xl" />
                      <span className="ml-2 md:text-md">Instagram</span>
                    </a>
                  </div>
                </motion.div>

                {/* Anggota Tim 2 */}
                <motion.div
                  className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
                >
                  <div className="avatar">
                    <div className="mask mask-squircle w-24 md:w-40">
                      <img src="/team/arief.jpeg" alt="Arief" />
                    </div>
                  </div>
                  <div className="text-left sm:text-center">
                    <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                      Arief Mahmud
                    </h3>
                    <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                      Steering Committee
                    </p>
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                    >
                      <FaInstagram className="text-lg md:text-xl" />
                      <span className="ml-2 md:text-md">Instagram</span>
                    </a>
                  </div>
                </motion.div>

                {/* Anggota Tim 3 */}
                <motion.div
                  className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
                >
                  <div className="avatar">
                    <div className="mask mask-squircle w-24 md:w-40">
                      <img src="/team/didint.png" alt="Didint" />
                    </div>
                  </div>
                  <div className="text-left sm:text-center">
                    <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                      Didint Prehantoro
                    </h3>
                    <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                      Event Director
                    </p>
                    <a
                      href="https://www.instagram.com/didintdwip"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                    >
                      <FaInstagram className="text-lg md:text-xl" />
                      <span className="ml-2 md:text-md">Instagram</span>
                    </a>
                  </div>
                </motion.div>

                {/* Anggota Tim 4 */}
                <motion.div
                  className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
                >
                  <div className="avatar">
                    <div className="mask mask-squircle w-24 md:w-40">
                      <img src="/team/lahabu.png" alt="Lahabu" />
                    </div>
                  </div>
                  <div className="text-left sm:text-center">
                    <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                      Haris Lahabu
                    </h3>
                    <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                      Race Director
                    </p>
                    <a
                      href="https://www.instagram.com/lahabu07"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                    >
                      <FaInstagram className="text-lg md:text-xl" />
                      <span className="ml-2 md:text-md">Instagram</span>
                    </a>
                  </div>
                </motion.div>

                {/* Anggota Tim 5 */}
                {/*  <motion.div
                  className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
                >
                  <div className="avatar">
                    <div className="mask mask-squircle w-24 md:w-40">
                      <img src="/team/efan.jpg" alt="Efan" />
                    </div>
                  </div>
                  <div className="text-left sm:text-center">
                    <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                      Efantri Febriansyah
                    </h3>
                    <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                      Technical Director
                    </p>
                    <a
                      href="https://www.instagram.com/xtrifeinnie"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                    >
                      <FaInstagram className="text-lg md:text-xl" />
                      <span className="ml-2 md:text-md">Instagram</span>
                    </a>
                  </div>
                </motion.div> */}
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:flex-nowrap sm:gap-0 gap-2 py-10 px-4 justify-center relative z-0">
              {/* Anggota Tim 1 */}
              <motion.div
                className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.0, ease: "easeOut" }}
              >
                <div className="avatar">
                  <div className="mask mask-squircle w-24 md:w-40">
                    <img src="/team/rudi.png" alt="Rudi" />
                  </div>
                </div>
                <div className="text-left sm:text-center">
                  <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                    Rudi Rohmansyah
                  </h3>
                  <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                    Senior Advisor
                  </p>
                  <a
                    href="https://www.instagram.com/rudroch"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                  >
                    <FaInstagram className="text-lg md:text-xl" />
                    <span className="ml-2 md:text-md">Instagram</span>
                  </a>
                </div>
              </motion.div>

              {/* Anggota Tim 2 */}
              <motion.div
                className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
              >
                <div className="avatar">
                  <div className="mask mask-squircle w-24 md:w-40">
                    <img src="/team/arief.jpeg" alt="Arief" />
                  </div>
                </div>
                <div className="text-left sm:text-center">
                  <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                    Arief Mahmud
                  </h3>
                  <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                    Steering Committee
                  </p>
                  <a
                    href="#"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                  >
                    <FaInstagram className="text-lg md:text-xl" />
                    <span className="ml-2 md:text-md">Instagram</span>
                  </a>
                </div>
              </motion.div>

              {/* Anggota Tim 3 */}
              <motion.div
                className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
              >
                <div className="avatar">
                  <div className="mask mask-squircle w-24 md:w-40">
                    <img src="/team/didint.png" alt="Didint" />
                  </div>
                </div>
                <div className="text-left sm:text-center">
                  <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                    Didint Prehantoro
                  </h3>
                  <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                    Event Director
                  </p>
                  <a
                    href="https://www.instagram.com/didintdwip"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                  >
                    <FaInstagram className="text-lg md:text-xl" />
                    <span className="ml-2 md:text-md">Instagram</span>
                  </a>
                </div>
              </motion.div>

              {/* Anggota Tim 4 */}
              <motion.div
                className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
              >
                <div className="avatar">
                  <div className="mask mask-squircle w-24 md:w-40">
                    <img src="/team/lahabu.png" alt="Lahabu" />
                  </div>
                </div>
                <div className="text-left sm:text-center">
                  <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                    Haris Lahabu
                  </h3>
                  <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                    Race Director
                  </p>
                  <a
                    href="https://www.instagram.com/lahabu07"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                  >
                    <FaInstagram className="text-lg md:text-xl" />
                    <span className="ml-2 md:text-md">Instagram</span>
                  </a>
                </div>
              </motion.div>

              {/* Anggota Tim 5 */}
              {/*  <motion.div
                className="flex items-center sm:flex-col sm:items-center space-x-4 sm:space-x-0 sm:space-y-2 w-full max-w-md sm:flex-1 sm:max-w-xs min-w-[340px] sm:min-w-[250px] sm:h-80 bg-white/5 rounded-xl p-6 sm:p-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
              >
                <div className="avatar">
                  <div className="mask mask-squircle w-24 md:w-40">
                    <img src="/team/efan.jpg" alt="Efan" />
                  </div>
                </div>
                <div className="text-left sm:text-center">
                  <h3 className="text-xl sm:text-lg uppercase font-bold mt-2 text-lime-600">
                    Efantri Febriansyah
                  </h3>
                  <p className="text-base sm:text-sm mt-1 text-white bg-lime-600 px-4 rounded-lg inline-block">
                    Technical Director
                  </p>
                  <a
                    href="https://www.instagram.com/xtrifeinnie"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center mt-1 text-lime-600 hover:text-lime-600/80 sm:justify-center"
                  >
                    <FaInstagram className="text-lg md:text-xl" />
                    <span className="ml-2 md:text-md">Instagram</span>
                  </a>
                </div>
              </motion.div> */}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
